import { useEffect, useRef, useState } from "react";
import { fromJS } from "immutable";
import { X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getItem } from "../api/ProjectApi";

const AttendeeItemView = ({ itemId, onRemove }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["getItem", itemId],
    queryFn: () => getItem(itemId),
    enabled: !!itemId,
    staleTime: 60 * 1000, // cache 60s
  });

  if (isLoading) {
    return (
      <div key={itemId} className="w-full flex items-center justify-between gap-2">
        <div className="flex items-center gap-x-2">
          <div className="relative w-6 h-6 overflow-hidden rounded-full bg-gray-200">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent" />
          </div>
          <div className="relative h-4 w-30 overflow-hidden rounded bg-gray-200">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent" />
          </div>
        </div>
        <div className="flex items-center gap-x-2">
          <div className="relative h-4 w-4 overflow-hidden rounded bg-gray-200">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent" />
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const contactOrCompany = fromJS(data);

  return (
    <div key={itemId} className="w-full flex items-center justify-between gap-2">
      <div className="flex items-center gap-x-2">
        {contactOrCompany.get("iconUrl") ? (
          <img
            className="h-6 w-6 flex-none rounded-full bg-gray-50"
            src={contactOrCompany.get("iconUrl")}
            alt={contactOrCompany.getIn(["data", "name", "value"])}
          />
        ) : (
          <div className="w-6 h-6 rounded-full">
            <svg className="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
        )}
        <div
          title={`${contactOrCompany.getIn(["data", "name", "value"])} (${contactOrCompany.getIn([
            "data",
            "email",
            "value",
          ])})`}
          className="text-sm font-semibold leading-6 text-gray-900"
        >
          {contactOrCompany.getIn(["data", "name", "value"])}
        </div>
        <div className="hidden sm:block ml-2 text-sm font-medium leading-6 text-gray-400">
          {contactOrCompany.getIn(["data", "email", "value"])}
        </div>
      </div>
      <div className="flex items-center gap-x-2">
        <X className="w-4 h-4 text-gray-500 cursor-pointer" onClick={() => onRemove(itemId)} />
      </div>
    </div>
  );
};

const ActivityAttendeeRenderItems = ({ eventData, handleChange }) => {
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [visibleCount, setVisibleCount] = useState(5);
  const loaderRef = useRef(null);

  useEffect(() => {
    if (eventData.itemIds) {
      const itemIdsData = eventData.itemIds.split(",");
      if (itemIdsData.length > 0) {
        setSelectedItemIds(itemIdsData);
      }
    } else {
      setSelectedItemIds([]);
    }
  }, [eventData.itemIds]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + 5, selectedItemIds.length));
        }
      },
      { threshold: 1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [selectedItemIds.length]);

  const handleRemove = (value) => {
    const itemIds = selectedItemIds.filter((itemId) => itemId !== value);
    handleChange("itemIds", itemIds.join(","));
  };

  const visibleItemIds = selectedItemIds.slice(0, visibleCount);

  return (
    <>
      {visibleItemIds.map((itemId) => (
        <AttendeeItemView key={itemId} itemId={itemId} onRemove={handleRemove} />
      ))}
      {visibleCount < selectedItemIds.length && (
        <div ref={loaderRef} className="py-4 text-center text-gray-500"></div>
      )}
    </>
  );
};

export default ActivityAttendeeRenderItems;
